import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import { validateContent, filterProfanity } from '../utils/contentModeration';
import { extractMentions, renderTextWithLinks } from '../utils/text';
import { createNotification } from '../utils/notificationHelpers';
import { createComment, deleteComment, likeComment, listComments, unlikeComment, updateComment } from '../utils/secureFunctions';
import ImageWithFallback from './ImageWithFallback';
import UserLink from './UserLink';
import VerifiedBadge from './VerifiedBadge';

type ContentType = 'post' | 'pulse' | 'testimony';

interface CommentDoc {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  userVerified?: boolean;
  content: string;
  likeCount: number;
  likedByMe: boolean;
  parentId?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface CommentSectionProps {
  contentId: string;
  contentType: ContentType;
  contentOwnerId?: string;
  onClose: () => void;
}

const CommentSection: React.FC<CommentSectionProps> = ({ contentId, contentType, contentOwnerId, onClose }) => {
  const [comments, setComments] = useState<CommentDoc[]>([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPhoto, setUserPhoto] = useState('');

  const refresh = async () => {
    try {
      const res = await listComments({ contentType, contentId, limit: 500 });
      setComments(res.comments as any);
    } catch (e) {
      console.error('Failed to load comments', e);
    }
  };

  useEffect(() => {
    const u = auth.currentUser;
    setUserName(u?.displayName || u?.email?.split('@')[0] || 'Anonymous');
    setUserPhoto(u?.photoURL || '');

    refresh();
    const t = window.setInterval(refresh, 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, contentType]);

  const roots = useMemo(() => comments.filter((c) => !c.parentId), [comments]);
  const repliesByParent = useMemo(() => {
    const map = new Map<string, CommentDoc[]>();
    for (const c of comments) {
      if (c.parentId) {
        if (!map.has(c.parentId)) map.set(c.parentId, []);
        map.get(c.parentId)!.push(c);
      }
    }
    return map;
  }, [comments]);

  const postComment = async (text: string, parentId?: string | null) => {
    if (!auth.currentUser) return;
    const v = validateContent(text);
    if (!v.isValid) {
      alert(v.message);
      return;
    }
    const safe = filterProfanity(text.trim());

    await createComment({
      contentType,
      contentId,
      content: safe,
      parentId: parentId || null,
      userName,
      userPhoto,
    });

    // Do not increment here: the VPS backend maintains the canonical comment counters
    // to prevent double-counting.

    if (contentOwnerId && contentOwnerId !== auth.currentUser.uid) {
      await createNotification(
        contentOwnerId,
        'comment',
        auth.currentUser.uid,
        userName || 'Someone',
        userPhoto || '',
        `${userName || 'Someone'} commented on your ${contentType}`,
        contentId,
        contentType,
        { snippet: safe.slice(0, 140) }
      );
    }

    try {
      const mentions = extractMentions(safe);
      // Mention-to-uid lookup previously relied on Firestore; skip for now.
      if (mentions.length) {
        // no-op
      }
    } catch (e) {
      console.warn('Mention notifications failed', e);
    }
  };

  const handleAdd = async (parentId?: string) => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await postComment(newComment, parentId);
      setNewComment('');
      setReplyingTo(null);
    } catch (e) {
      console.error(e);
      alert('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (commentId: string) => {
    if (!auth.currentUser) return;
    const c = comments.find((x) => x.id === commentId);
    const liked = !!c?.likedByMe;
    if (liked) {
      await unlikeComment(commentId);
    } else {
      await likeComment(commentId);
    }
    await refresh();
  };

  const handleEdit = async (commentId: string) => {
    const text = editContent.trim();
    if (!text) return;
    const v = validateContent(text);
    if (!v.isValid) {
      alert(v.message);
      return;
    }
    const safe = filterProfanity(text);
    await updateComment(commentId, { content: safe });
    setEditingId(null);
    setEditContent('');
    await refresh();
  };

  const handleDelete = async (commentId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this comment?')) return;
    await deleteComment(commentId);
    await refresh();
  };

  const CommentItem: React.FC<{ c: CommentDoc; isReply?: boolean }> = ({ c, isReply }) => (
    <div key={c.id} style={{ ...styles.comment, marginLeft: isReply ? 40 : 0 }}>
      <ImageWithFallback src={c.userPhoto} alt="" style={styles.avatar} />
      <div style={styles.commentContent}>
        <UserLink as="strong" userId={c.userId} style={styles.commentUser}>
          {c.userName}
          {(() => {
            const explicit = typeof c.userVerified === 'boolean' ? c.userVerified : undefined;
            const flag = typeof explicit === 'boolean' ? explicit : false;
            return flag ? <VerifiedBadge size={12} /> : null;
          })()}
        </UserLink>

        {editingId === c.id ? (
          <div style={{ marginTop: 6 }}>
            <input
              type="text"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={styles.input}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => handleEdit(c.id)} style={styles.smallButton}>Save</button>
              <button onClick={() => { setEditingId(null); setEditContent(''); }} style={styles.smallButtonSecondary}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <p style={styles.commentText}>{renderTextWithLinks(c.content)}</p>
            {c.updatedAt && <span style={styles.editedLabel}>(edited)</span>}
          </>
        )}

        <div style={styles.commentActions}>
          <span style={styles.commentTime}>{c.createdAt ? new Date(c.createdAt).toLocaleString() : 'Just now'}</span>
          <button onClick={() => handleLike(c.id)} style={styles.commentActionButton}>
            {c.likedByMe ? '❤️' : '🤍'} {c.likeCount}
          </button>
          {!isReply && (
            <button onClick={() => setReplyingTo(c.id)} style={styles.commentActionButton}>Reply</button>
          )}
          {c.userId === auth.currentUser?.uid && (
            <>
              <button onClick={() => { setEditingId(c.id); setEditContent(c.content); }} style={styles.commentActionButton}>Edit</button>
              <button onClick={() => handleDelete(c.id)} style={styles.commentActionButton}>Delete</button>
            </>
          )}
        </div>

        {replyingTo === c.id && (
          <div style={{ marginTop: 8 }}>
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd(c.id)}
              placeholder={`Reply to ${c.userName}...`}
              style={styles.input}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button onClick={() => handleAdd(c.id)} disabled={posting} style={styles.smallButton}>{posting ? '...' : 'Reply'}</button>
              <button onClick={() => { setReplyingTo(null); setNewComment(''); }} style={styles.smallButtonSecondary}>Cancel</button>
            </div>
          </div>
        )}

        {repliesByParent.get(c.id)?.map((r) => (
          <CommentItem key={r.id} c={r} isReply />
        ))}
      </div>
    </div>
  );

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Comments</h3>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.commentsList}>
          {roots.length === 0 ? (
            <p style={styles.emptyText}>No comments yet. Be the first!</p>
          ) : (
            roots.map((c) => <CommentItem key={c.id} c={c} />)
          )}
        </div>

        <div style={styles.inputSection}>
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !replyingTo && handleAdd()}
            placeholder="Write a comment..."
            style={styles.input}
          />
          <button onClick={() => handleAdd()} disabled={posting || !newComment.trim()} style={styles.postButton}>
            {posting ? '...' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid #E0E0E0',
  },
  title: { margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#072B2C' },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#5B6E6E',
    padding: 0,
    width: '30px',
    height: '30px',
  },
  commentsList: { flex: 1, overflowY: 'auto', padding: '15px 20px' },
  emptyText: { textAlign: 'center', color: '#5B6E6E', fontSize: '14px', padding: '20px' },
  comment: { display: 'flex', gap: '10px', marginBottom: '15px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  commentContent: { flex: 1 },
  commentUser: { fontSize: '14px', color: '#072B2C', marginBottom: '4px' },
  commentText: { fontSize: '14px', color: '#072B2C', margin: '4px 0', lineHeight: '1.4' },
  editedLabel: { fontSize: '11px', color: '#999', marginLeft: 6 },
  commentActions: { display: 'flex', gap: '12px', alignItems: 'center', marginTop: 4 },
  commentTime: { fontSize: '11px', color: '#5B6E6E' },
  commentActionButton: { background: 'none', border: 'none', color: '#0B6B6F', fontSize: '12px', cursor: 'pointer', padding: '2px 6px' },
  inputSection: { display: 'flex', gap: '10px', padding: '15px 20px', borderTop: '1px solid #E0E0E0' },
  input: { flex: 1, padding: '10px', fontSize: '14px', borderRadius: '20px', border: '1px solid #E0E0E0', outline: 'none' },
  postButton: { padding: '10px 20px', backgroundColor: '#17A2A9', color: '#FFFFFF', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  smallButton: { padding: '4px 12px', backgroundColor: '#17A2A9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  smallButtonSecondary: { padding: '4px 12px', backgroundColor: '#E0E0E0', color: '#072B2C', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
};

export default CommentSection;


